import { useState } from "react";
import { ArrowUpRight, BookOpen, Globe, Link as LinkIcon, Maximize2, ShoppingCart, Sparkles, X } from "lucide-react";
import { useMediaLookupAddingMore } from "@/hooks/voice/useMediaLookupPending";
import { trackEvent } from "@/lib/ga4";
import { cn } from "@/lib/utils";
import type { GenieAttachment } from "@/types/voice/genie-attachment";
import { GenieImageLightbox } from "./GenieImageLightbox";
import { isSafeUrl } from "./infoLinkIcons";

const SOURCE_ICON = {
  resource: LinkIcon,
  knowledge: BookOpen,
  product: ShoppingCart,
  web: Globe,
} as const;

const hostnameOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

interface GenieAttachmentCardProps {
  attachment: GenieAttachment;
  brandColor: string;
  /**
   * "chat": inline in a transcript. "avatar": inside GenieSharedMedia under
   * the voice avatar (more compact picture). "dashboard": conversation history.
   */
  variant?: "chat" | "avatar" | "dashboard";
  agentId?: string | null;
  className?: string;
}

/**
 * A link or picture the Genie put on screen mid-conversation (showRelatedMedia).
 * Renders nothing when the URL isn't http(s) or an image fails to load, so a
 * dead asset never leaves a broken frame in the chat.
 */
export const GenieAttachmentCard = ({
  attachment,
  brandColor,
  variant = "chat",
  agentId,
  className,
}: GenieAttachmentCardProps) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  if (!isSafeUrl(attachment.url) || (attachment.kind === "image" && imageFailed)) return null;

  const SourceIcon = SOURCE_ICON[attachment.source] ?? LinkIcon;
  const hostname = hostnameOf(attachment.url);
  const previewUrl = attachment.kind === "link" && attachment.imageUrl && !previewFailed ? attachment.imageUrl : null;

  const handleOpen = () => {
    if (variant === "dashboard") return;
    trackEvent("genie_attachment_opened", {
      attachment_kind: attachment.kind,
      attachment_source: attachment.source,
      surface: variant,
      agent_id: agentId ?? undefined,
    });
  };

  const isAvatar = variant === "avatar";

  const card =
    attachment.kind === "image" ? (
      <button
        type="button"
        onClick={() => {
          handleOpen();
          setIsLightboxOpen(true);
        }}
        aria-label={`View ${attachment.title} full screen`}
        className="group block w-full cursor-zoom-in overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white text-left transition-shadow hover:shadow-[0_12px_32px_-16px_rgba(34,43,69,0.35)]"
      >
        <div className="flex items-center justify-center bg-[#F9FAFC]">
          <img
            src={attachment.url}
            alt={attachment.title}
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setImageFailed(true)}
            className={cn("w-full object-contain", isAvatar ? "max-h-44" : "max-h-60")}
          />
        </div>
        <div className="flex min-h-[44px] items-center gap-2 px-3.5 py-2">
          <SourceIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: brandColor }} aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700">{attachment.title}</span>
          <Maximize2
            className="h-4 w-4 flex-shrink-0 text-gray-500 transition-transform group-hover:scale-110"
            aria-hidden="true"
          />
        </div>
      </button>
    ) : (
      <a
        href={attachment.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleOpen}
        className="group flex min-h-[44px] items-stretch overflow-hidden rounded-2xl border border-[#E5EAF0] bg-white transition-shadow hover:shadow-[0_12px_32px_-16px_rgba(34,43,69,0.35)]"
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt=""
            loading="lazy"
            referrerPolicy="no-referrer"
            onError={() => setPreviewFailed(true)}
            className="w-20 flex-shrink-0 bg-[#F9FAFC] object-cover sm:w-24"
          />
        ) : (
          <span
            className="flex w-14 flex-shrink-0 items-center justify-center"
            style={{ backgroundColor: `${brandColor}14` }}
          >
            <SourceIcon className="h-5 w-5" style={{ color: brandColor }} aria-hidden="true" />
          </span>
        )}
        <span className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-3.5 py-2.5 text-left">
          <span className="line-clamp-2 text-sm font-semibold text-[#222B45]">{attachment.title}</span>
          {attachment.description && (
            <span className="line-clamp-2 text-xs text-gray-600">{attachment.description}</span>
          )}
          <span className="truncate text-xs text-gray-500">{hostname}</span>
        </span>
        <span className="flex items-center pr-3.5">
          <ArrowUpRight
            className="h-4 w-4 text-gray-500 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
            style={{ color: brandColor }}
            aria-hidden="true"
          />
        </span>
      </a>
    );

  return (
    <div
      className={cn(
        "w-full max-w-sm",
        variant === "chat" && "animate-in fade-in slide-in-from-bottom-2 duration-300",
        className,
      )}
    >
      {card}
      {attachment.kind === "image" && (
        <GenieImageLightbox
          imageUrl={attachment.url}
          title={attachment.title}
          sourceUrl={attachment.pageUrl && isSafeUrl(attachment.pageUrl) ? attachment.pageUrl : null}
          isOpen={isLightboxOpen}
          onClose={() => setIsLightboxOpen(false)}
        />
      )}
    </div>
  );
};

interface GenieAttachmentPendingProps {
  brandColor: string;
  className?: string;
}

/**
 * Quiet status line shown while the Genie is still looking up a link or
 * picture (useMediaLookupPending). Kept small since lookups often find nothing.
 */
export const GenieAttachmentPending = ({ brandColor, className }: GenieAttachmentPendingProps) => {
  const addingMore = useMediaLookupAddingMore();

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn("inline-flex items-center gap-1.5 py-1 text-xs text-gray-500 animate-in fade-in duration-500", className)}
    >
      <Sparkles
        className="h-3.5 w-3.5 flex-shrink-0 animate-pulse motion-reduce:animate-none"
        style={{ color: brandColor }}
        aria-hidden="true"
      />
      <span>{addingMore ? "Looking for more…" : "Looking for something to show…"}</span>
    </div>
  );
};

interface GenieSharedMediaProps {
  attachments: GenieAttachment[];
  brandColor: string;
  agentName?: string | null;
  agentId?: string | null;
  onDismiss: () => void;
  /** A lookup is still running: show the placeholder card under whatever is already shared. */
  pending?: boolean;
  className?: string;
}

/**
 * What the Genie just shared, floated under the voice avatar while the
 * transcript is closed — so a visitor mid-voice-call sees the link or picture
 * without opening the chat. The same items are also in the transcript.
 */
export const GenieSharedMedia = ({
  attachments,
  brandColor,
  agentName,
  agentId,
  onDismiss,
  pending = false,
  className,
}: GenieSharedMediaProps) => {
  if (attachments.length === 0 && !pending) return null;

  if (attachments.length === 0) {
    return (
      <GenieAttachmentPending
        brandColor={brandColor}
        className={cn("rounded-full bg-white/80 px-3 shadow-sm backdrop-blur", className)}
      />
    );
  }

  return (
    <div
      className={cn(
        "w-full max-w-sm rounded-3xl bg-white/80 p-2 shadow-[0_24px_60px_-28px_rgba(34,43,69,0.45)] backdrop-blur animate-in fade-in zoom-in-95 slide-in-from-bottom-3 duration-500",
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 pl-2">
        <span className="truncate text-xs font-medium text-gray-500">
          {agentName ? `${agentName} shared` : "Shared with you"}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-black/[0.04] hover:text-gray-700"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2">
        {attachments.map((attachment) => (
          <GenieAttachmentCard
            key={`${attachment.kind}:${attachment.url}`}
            attachment={attachment}
            brandColor={brandColor}
            variant="avatar"
            agentId={agentId}
          />
        ))}
        {pending && <GenieAttachmentPending brandColor={brandColor} />}
      </div>
    </div>
  );
};
